/**
 * Éditeur de modèle de document RH.
 * Layout : éditeur TipTap à gauche, panneau "Variables" à droite.
 */
import React, { useEffect, useMemo } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import {
  Bold, Italic, List, ListOrdered, Heading1, Heading2, Heading3,
  Minus, Undo2, Redo2, AlignLeft, AlignCenter, AlignRight, AlignJustify,
  Variable as VariableIcon, Eye,
} from 'lucide-react';
import { VariableNode } from './VariableNode';

const MenuButton = ({ active, onClick, children, title }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    className={`p-1.5 rounded transition-colors ${
      active ? 'bg-primary/20 text-primary' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
    }`}
  >
    {children}
  </button>
);

export default function DocumentTemplateEditor({
  content,
  onChange,
  variables = [],          // VARIABLE_TREE depuis le backend
  onPreview,               // callback bouton "Aperçu"
  disabled,
}) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Placeholder.configure({ placeholder: 'Composez votre modèle de document...' }),
      VariableNode,
    ],
    content: content || '<p></p>',
    editable: !disabled,
    onUpdate: ({ editor: e }) => onChange && onChange(e.getHTML()),
  });

  useEffect(() => {
    if (editor && content !== undefined && editor.getHTML() !== content) {
      editor.commands.setContent(content || '<p></p>');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, editor]);

  useEffect(() => {
    if (editor) editor.setEditable(!disabled);
  }, [disabled, editor]);

  const insertVariable = (group, item) => {
    if (!editor) return;
    editor
      .chain()
      .focus()
      .insertVariable({
        key: `${group.group}.${item.key}`,
        label: item.label,
        color: group.color,
      })
      .run();
  };

  const groups = useMemo(() => variables || [], [variables]);

  if (!editor) return null;

  return (
    <div className="flex gap-4 h-full">
      {/* Éditeur */}
      <div className="flex-1 flex flex-col border rounded-lg overflow-hidden bg-white">
        <div className="flex items-center gap-0.5 px-2 py-1 border-b bg-gray-50 flex-wrap">
          <MenuButton active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} title="Gras">
            <Bold size={15} />
          </MenuButton>
          <MenuButton active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} title="Italique">
            <Italic size={15} />
          </MenuButton>

          <div className="w-px h-5 bg-gray-200 mx-1" />

          <MenuButton active={editor.isActive('heading', { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} title="Titre 1">
            <Heading1 size={15} />
          </MenuButton>
          <MenuButton active={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="Titre 2">
            <Heading2 size={15} />
          </MenuButton>
          <MenuButton active={editor.isActive('heading', { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} title="Titre 3">
            <Heading3 size={15} />
          </MenuButton>

          <div className="w-px h-5 bg-gray-200 mx-1" />

          <MenuButton active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Liste à puces">
            <List size={15} />
          </MenuButton>
          <MenuButton active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Liste numérotée">
            <ListOrdered size={15} />
          </MenuButton>

          <div className="w-px h-5 bg-gray-200 mx-1" />

          <MenuButton active={editor.isActive({ textAlign: 'left' })} onClick={() => editor.chain().focus().setTextAlign('left').run()} title="Aligner à gauche">
            <AlignLeft size={15} />
          </MenuButton>
          <MenuButton active={editor.isActive({ textAlign: 'center' })} onClick={() => editor.chain().focus().setTextAlign('center').run()} title="Centrer">
            <AlignCenter size={15} />
          </MenuButton>
          <MenuButton active={editor.isActive({ textAlign: 'right' })} onClick={() => editor.chain().focus().setTextAlign('right').run()} title="Aligner à droite">
            <AlignRight size={15} />
          </MenuButton>
          <MenuButton active={editor.isActive({ textAlign: 'justify' })} onClick={() => editor.chain().focus().setTextAlign('justify').run()} title="Justifier">
            <AlignJustify size={15} />
          </MenuButton>

          <div className="w-px h-5 bg-gray-200 mx-1" />

          <MenuButton onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Séparateur">
            <Minus size={15} />
          </MenuButton>

          <div className="flex-1" />

          {onPreview && (
            <button
              type="button"
              onClick={onPreview}
              className="px-2 py-1 text-xs font-medium text-primary hover:bg-primary/10 rounded inline-flex items-center gap-1"
            >
              <Eye size={13} /> Aperçu
            </button>
          )}

          <MenuButton onClick={() => editor.chain().focus().undo().run()} title="Annuler">
            <Undo2 size={15} />
          </MenuButton>
          <MenuButton onClick={() => editor.chain().focus().redo().run()} title="Rétablir">
            <Redo2 size={15} />
          </MenuButton>
        </div>

        <EditorContent
          editor={editor}
          className="prose prose-sm max-w-none px-6 py-4 flex-1 overflow-auto focus:outline-none
            [&_.ProseMirror]:outline-none [&_.ProseMirror]:min-h-[300px]
            [&_.ProseMirror_p.is-editor-empty:first-child::before]:text-gray-300
            [&_.ProseMirror_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)]
            [&_.ProseMirror_p.is-editor-empty:first-child::before]:float-left
            [&_.ProseMirror_p.is-editor-empty:first-child::before]:pointer-events-none
            [&_.ProseMirror_p.is-editor-empty:first-child::before]:h-0"
        />
      </div>

      {/* Panneau Variables */}
      <div className="w-72 flex flex-col border rounded-lg bg-white overflow-hidden">
        <div className="px-3 py-2 border-b bg-gray-50 flex items-center gap-1.5 text-sm font-semibold text-gray-700">
          <VariableIcon size={14} className="text-primary" />
          Variables
        </div>
        <div className="flex-1 overflow-auto p-2 space-y-3">
          {groups.map((g) => (
            <div key={g.group}>
              <div
                className="text-[10px] font-bold uppercase tracking-wide px-1 mb-1"
                style={{ color: g.color }}
              >
                {g.label}
              </div>
              <div className="flex flex-wrap gap-1">
                {g.items.map((it) => (
                  <button
                    key={it.key}
                    type="button"
                    onClick={() => insertVariable(g, it)}
                    disabled={disabled}
                    title={`Insérer ${g.label} → ${it.label}`}
                    className="text-[11px] px-2 py-0.5 rounded border transition hover:scale-[1.02] disabled:opacity-40"
                    style={{
                      background: `${g.color}1a`,
                      color: g.color,
                      borderColor: `${g.color}55`,
                    }}
                  >
                    {it.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="px-3 py-2 border-t bg-gray-50 text-[11px] text-gray-500 leading-tight">
          Clique sur une variable pour l'insérer à l'emplacement du curseur.
          Les valeurs réelles seront renseignées au moment de la génération du document.
        </div>
      </div>
    </div>
  );
}
